import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Select, Radio, Button, Modal, Input, Tag, message, Spin, Empty, Space } from "antd";
import { PlusOutlined, ArrowLeftOutlined, EditOutlined } from "@ant-design/icons";
import practiceService from "../services/PracticeService";

/**
 * P2 M2 (Items 1, 2, 3) -- admin screen to create practices and set each one's
 * default note style and AI-off sections.
 *
 * Style options mirror the backend section styles: verbose | concise | bullet_points.
 * AI-off section ids are entered as a tags multi-select of known section_ids.
 */
const STYLE_OPTIONS = [
  { label: "Verbose", value: "verbose" },
  { label: "Concise", value: "concise" },
  { label: "Bullet", value: "bullet_points" },
];

export default function PracticeDefaults() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [practices, setPractices] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [style, setStyle] = useState("concise");
  const [aiOffIds, setAiOffIds] = useState([]);
  const [saving, setSaving] = useState(false);

  // Create-practice modal
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  // Rename modal
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameName, setRenameName] = useState("");
  const [renaming, setRenaming] = useState(false);

  // Archive/restore
  const [toggling, setToggling] = useState(false);

  const applySelection = (rows, id) => {
    const p = rows.find((x) => x.id === id);
    setSelectedId(id);
    setStyle(p?.default_style || "concise");
    setAiOffIds(p?.default_ai_off_section_ids || []);
  };

  const loadPractices = async (selectId) => {
    const rows = await practiceService.listPractices();
    const list = Array.isArray(rows) ? rows : [];
    setPractices(list);
    if (list.length > 0) {
      applySelection(list, selectId ?? list[0].id);
    } else {
      setSelectedId(null);
    }
    return list;
  };

  useEffect(() => {
    (async () => {
      try {
        await loadPractices();
      } catch (e) {
        message.error("Failed to load practices");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onSelectPractice = (id) => applySelection(practices, id);

  const onSave = async () => {
    if (selectedId == null) return;
    setSaving(true);
    try {
      await practiceService.updateDefaults(selectedId, {
        default_style: style,
        default_ai_off_section_ids: aiOffIds,
      });
      message.success("Defaults saved");
    } catch (e) {
      message.error("Failed to save defaults");
    } finally {
      setSaving(false);
    }
  };

  const onCreate = async () => {
    const name = newName.trim();
    if (!name) {
      message.warning("Enter a practice name.");
      return;
    }
    setCreating(true);
    try {
      const created = await practiceService.createPractice({
        name,
        default_style: "concise",
      });
      message.success(`Created "${name}".`);
      setCreateOpen(false);
      setNewName("");
      await loadPractices(created?.id);
    } catch (e) {
      message.error(
        e?.response?.data?.detail || e?.message || "Failed to create practice."
      );
    } finally {
      setCreating(false);
    }
  };

  const openRename = () => {
    const current = practices.find((p) => p.id === selectedId);
    setRenameName(current?.name || "");
    setRenameOpen(true);
  };

  const onRename = async () => {
    const name = renameName.trim();
    if (!name) {
      message.warning("Enter a practice name.");
      return;
    }
    setRenaming(true);
    try {
      await practiceService.updatePractice(selectedId, { name });
      message.success(`Renamed to "${name}".`);
      setRenameOpen(false);
      setRenameName("");
      await loadPractices(selectedId);
    } catch (e) {
      if (e?.response?.status === 409) {
        // 409 = name conflict: use a more specific fallback when the server detail is absent.
        message.error(e?.response?.data?.detail || "A practice with that name already exists.");
      } else {
        message.error(e?.response?.data?.detail || e?.message || "Failed to rename practice.");
      }
    } finally {
      setRenaming(false);
    }
  };

  const onToggleActive = () => {
    const current = practices.find((p) => p.id === selectedId);
    if (!current) return;

    if (current.is_active) {
      // Archiving: require confirmation
      Modal.confirm({
        title: "Archive this practice?",
        content: `"${current.name}" will be marked inactive. Clinicians assigned to it will no longer see it as active.`,
        okText: "Archive",
        okType: "danger",
        cancelText: "Cancel",
        onOk: () => doToggleActive(current),
      });
    } else {
      // Restoring: no confirmation needed
      doToggleActive(current);
    }
  };

  const doToggleActive = async (current) => {
    setToggling(true);
    try {
      await practiceService.updatePractice(current.id, { is_active: !current.is_active });
      const action = current.is_active ? "archived" : "restored";
      message.success(`Practice ${action}.`);
      await loadPractices(current.id);
    } catch (e) {
      message.error(e?.response?.data?.detail || e?.message || "Failed to update practice.");
    } finally {
      setToggling(false);
    }
  };

  if (loading) {
    return <Spin />;
  }

  const selectedPractice = practices.find((p) => p.id === selectedId);
  const isActive = selectedPractice?.is_active ?? true;

  const cardTitle = (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {/*
       * No unsaved-changes guard here: saving is an explicit "Save defaults"
       * action, edits are trivially redoable, and this page has no dirty-tracking
       * state. This is intentional, not an oversight (cf. ActionBar.js which
       * does guard because it auto-saves in-session transcript changes).
       */}
      <Button
        size="small"
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate("/")}
        aria-label="Back to main menu"
      >
        Back
      </Button>
      <span>Practice Defaults</span>
    </div>
  );

  return (
    <Card
      title={cardTitle}
      style={{ maxWidth: 640, margin: "24px auto" }}
      extra={
        practices.length > 0 ? (
          <Button icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            New practice
          </Button>
        ) : null
      }
    >
      {practices.length === 0 ? (
        <Empty
          description="No practices yet. Create one to set its default note style and AI-off sections, and to assign clinicians to it."
        >
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            New practice
          </Button>
        </Empty>
      ) : (
        <>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", marginBottom: 8 }}>Practice</label>
            <Space wrap align="center">
              <Select
                style={{ minWidth: 240 }}
                value={selectedId}
                onChange={onSelectPractice}
                options={practices.map((p) => ({
                  label: p.is_active === false ? `${p.name} (inactive)` : p.name,
                  value: p.id,
                }))}
              />
              {selectedPractice && (
                <>
                  <Tag color={isActive ? "success" : "default"} data-testid="practice-status-tag">
                    {isActive ? "Active" : "Inactive"}
                  </Tag>
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={openRename}
                    aria-label="Rename practice"
                  >
                    Rename
                  </Button>
                  <Button
                    size="small"
                    danger={isActive}
                    loading={toggling}
                    onClick={onToggleActive}
                    aria-label={isActive ? "Archive practice" : "Restore practice"}
                  >
                    {isActive ? "Archive" : "Restore"}
                  </Button>
                </>
              )}
            </Space>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", marginBottom: 8 }}>Default note style</label>
            <Radio.Group value={style} onChange={(e) => setStyle(e.target.value)}>
              {STYLE_OPTIONS.map((o) => (
                <Radio key={o.value} value={o.value} aria-label={o.label}>
                  {o.label}
                </Radio>
              ))}
            </Radio.Group>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", marginBottom: 8 }}>
              AI-off sections (clinician-only)
            </label>
            <Select
              mode="tags"
              style={{ width: "100%" }}
              placeholder="section_id values that should skip AI generation"
              value={aiOffIds}
              onChange={setAiOffIds}
            />
          </div>

          <Button type="primary" loading={saving} onClick={onSave}>
            Save defaults
          </Button>
        </>
      )}

      {/* Create practice modal */}
      <Modal
        title="New practice"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={onCreate}
        okText="Create"
        confirmLoading={creating}
      >
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Practice name"
          onPressEnter={onCreate}
        />
      </Modal>

      {/* Rename practice modal */}
      <Modal
        title="Rename practice"
        open={renameOpen}
        onCancel={() => { setRenameOpen(false); setRenameName(""); }}
        onOk={onRename}
        okText="Rename"
        confirmLoading={renaming}
      >
        <Input
          value={renameName}
          onChange={(e) => setRenameName(e.target.value)}
          placeholder="New practice name"
          onPressEnter={onRename}
        />
      </Modal>
    </Card>
  );
}
